import { FC, JSX, useContext, useEffect, useRef, useState } from "react";
import { CountyProjectContext, RegionFilterItem } from "../../CountyProjectContext";
import { MappingContext } from "@app/mapping/MappingContext";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import ProviderMemberLayerControl, { ProviderMemberLayerControlConfig } from "./controls/ProviderMemberLayerControl";
import ProviderMemberLegendControl, { ProviderMemberLegendControlConfig } from "./controls/ProviderMemberLegendControl";
import ProjectService from "../../../projects/projectService";
import MemberSetService from "../../../members/memberSetService";
import { ServicingProvider } from "../../../projects/results/servicing-provider";
import { ProjectOptions } from "@app/projects";
import { FilterType, NetworkAssessmentAnalysisType, ProviderNetworkType, RegionSetStatusType } from "../../enumerators";
import { Feature, FeatureCollection, Point } from "geojson";
import mapboxgl, { LngLatLike } from "mapbox-gl";
import ServicingProviderProfile from "../../servicing-provider/ServicingProviderProfile";
import { Flyout, FlyoutContext } from "@app/shared/Flyouts";
import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";
import { GainsPercentEnum } from "../../servicing-provider/types";
import { AdequacySummaryByRegionSet, AdequacySummarySpecialtyGroup } from "../../results/adequacy";
import MapDetailsBox from "./MapDetailsBox";
import MapExport from "./MapExport";
import { bbox } from "@turf/bbox";
import GeographyService from "../../../geography/geographyService";
import LoadingSpinner from "../../../shared/LoadingSpinner";
import { useHistory, useLocation } from "react-router-dom";
import { MemberMapResults, NetworkProviderMapResults } from '@app/projects/results/common/mapping'
import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import "mapbox-gl/dist/mapbox-gl.css";

const SkeletonMaps: FC = (): JSX.Element => {
  return (<span className="skeleton shimmer">
    <div className="d-flex mb-3 row justify-content-start">
      <div className="col-2">
        <div className="bone p-2" style={{ height: "36px" }}></div>
      </div>
      <div className="col-3">
        <div className="bone p-2" style={{ height: "36px" }}></div>
      </div>
      <div className="col-2">
        <div className="bone p-2" style={{ height: "36px" }}></div>
      </div>
      <span className="d-flex justify-content-end col-12 col-xl">
        <div className="col-3">
          <div className="bone p-2" style={{ height: "36px" }}></div>
        </div>
        <div className="col-3">
          <div className="bone p-2" style={{ height: "36px" }}></div>
        </div>
      </span>
    </div>
    <div className="d-flex mb-3 row justify-content-start">
      <div className="col-12">
        <div className="bone p-2" style={{ height: "512px" }}></div>
      </div>
    </div>
  </span>);
}

const WrappedProviderMemberMapping: FC<React.PropsWithChildren<unknown>> = (): JSX.Element => {
  const projectContext = useContext(CountyProjectContext);
  const mappingContext = useContext(MappingContext);
  const flyoutContext = useContext(FlyoutContext);
  const featureFlagContext = useContext(FeatureFlagContext);

  if (!projectContext?.runConfiguration) {
    return <SkeletonMaps />;
  }

  // Mapbox internal properties for clustering
  const MAPBOX_CLUSTER_POINT_COUNT = "point_count";
  const MAPBOX_CLUSTER_POINT_COUNT_ABBREVIATED = "point_count_abbreviated";

  // Mapbox types
  const MAPBOX_TYPE_CIRCLE: string = "circle";
  const MAPBOX_TYPE_GEOJSON: string = "geojson";
  const MAPBOX_TYPE_LINE: string = "line";
  const MAPBOX_TYPE_SYMBOL: string = "symbol";

  // Image constants
  const MEMBERS_WITH_IMAGE: string = "members-with-image";
  const MEMBERS_WITHOUT_IMAGE: string = "members-without-image";
  const MULTIPLE_PROVIDERS_IMAGE: string = "multiple-providers-image";
  const PROVIDER_HIGHLIGHT_IMAGE: string = "provider-highlight-image";
  const PROVIDER_IMAGE: string = "provider-image";

  // Image PNG filename constants
  const MEMBERS_WITH_PNG: string = "members-with.png";
  const MEMBERS_WITHOUT_PNG: string = "members-without.png";
  const MULTIPLE_PROVIDERS_PNG: string = "multiple-providers.png";
  const PROVIDER_PNG: string = "provider.png";
  const PROVIDER_HIGHLIGHT_PNG: string = "provider-highlight.png";

  const projectService = new ProjectService();
  const memberSetService = new MemberSetService();
  const geographyService = new GeographyService();

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef(null);

  const [latitude, setLatitude] = useState<number>(mappingContext.defaultLatitude);
  const [longitude, setLongitude] = useState<number>(mappingContext.defaultLongitude);
  const [pitch, setPitch] = useState<number>(mappingContext.defaultPitch);
  const [zoom, setZoom] = useState<number>(mappingContext.defaultZoom);
  const [bearing, setBearing] = useState<number>(mappingContext.defaultBearing);

  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('');
  const [selectedServiceArea, setSelectedServiceArea] = useState<string>('');
  const [selectedProviderSource, setSelectedProviderSource] = useState<ProviderNetworkType>(ProviderNetworkType.Network);

  const [provider, setProvider] = useState<ServicingProvider>(null);
  const [project, setProject] = useState<ProjectOptions>(null);

  const [flyoutHasOpened, setFlyoutHasOpened] = useState<boolean>(false);

  const clusteredMembersWithAccessSource: string = 'members-with-source';
  const clusteredMembersWithoutAccessSource: string = 'members-without-source';
  const providersSource: string = 'providers-source';
  const countyBordersSource: string = 'county-borders-source';
  const unclusteredMembersSource: string = 'unclustered-members-source';

  const providersClusterMaxZoom = 16;
  const providersClusterRadius = 25;

  const membersClusterMaxZoom = 14;
  const membersClusterRadius = 45;

  const clusterRadiusSmall = 25;
  const clusterRadiusMedium = 35;
  const clusterRadiusLarge = 50;
  const clusterRadiusLowerStep = 100;
  const clusterRadiusUpperStep = 750;

  const clusteredMembersWithAccessPointLayer: string = 'members-with-access-layer';
  const clusteredMembersWithAccessClusteredLayer: string = 'members-with-access-clustered-layer';
  const clusteredMembersWithAccessClusterCountLayer: string = 'members-with-access-cluster-count-layer';
  const clusteredMembersWithoutAccessPointLayer: string = 'members-without-access-layer';
  const clusteredMembersWithoutAccessClusteredLayer: string = 'members-without-access-clustered-layer';
  const clusteredMembersWithoutAccessClusterCountLayer: string = 'members-without-access-cluster-count-layer';
  const singleProvidersLayer: string = 'single-providers-layer';
  const multipleProvidersLayer: string = 'multiple-providers-layer';
  const countyBordersLayer: string = 'county-borders-layer';
  const unclusteredMembersWithAccessLayer: string = 'unclustered-members-with-access-layer';
  const unclusteredMembersWithoutAccessLayer: string = 'unclustered-members-without-access-layer';

  const imagesDir = `${window.customUIBaseUrl}/images/`;
  const defaultMapStyle = 'mapbox://styles/mapbox/light-v11';

  const sortedSpecialtyGroups = (projectContext.runConfiguration.specialtyGroups || []).sort((a, b) => a.name.localeCompare(b.name));

  const [membersLayerInitialized, setMembersLayerInitialized] = useState(false);
  const memberData = useRef<FeatureCollection<Point, MemberMapResults>>(null);
  const [providersLayerInitialized, setProvidersLayerInitialized] = useState(false);
  const [selectedMapStyle, setSelectedMapStyle] = useState<string>(defaultMapStyle);
  const [memberIsLoading, setMemberIsLoading] = useState(false);
  const [providerIsLoading, setProviderIsLoading] = useState(false);
  const providerData = useRef<FeatureCollection<Point, NetworkProviderMapResults>>(null);
  const [bordersAreLoading, setBordersAreLoading] = useState(false);
  const borderData = useRef<FeatureCollection>(null);
  const [countyBorderLayerInitialized, setCountyBorderLayerInitialized] = useState(false);
  const [mapButtonsAreDisabled, setMapButtonsAreDisabled] = useState(true);
  const [countyBordersSourceBBox, setCountyBordersSourceBBox] = useState(null);
  const [providersSourceBBox, setProvidersSourceBBox] = useState(null);
  const [memberSourceBBox, setMemberSourceBBox] = useState(null);
  const [clusterMembers, setClusterMembers] = useState(false);

  const isLoading = memberIsLoading || providerIsLoading || bordersAreLoading;

  const [currentAdequacySummarySpecialtyGroup, setCurrentAdequacySummarySpecialtyGroup] = useState<AdequacySummarySpecialtyGroup | null>(null);

  const singleProviderPopup = new mapboxgl.Popup({
    className: 'provider-member-mapping__single-provider-popup',
    closeButton: false,
    closeOnClick: false
  });

  const multiProviderPopup = new mapboxgl.Popup({
    className: 'provider-member-mapping__multi-provider-popup',
    closeButton: true,
    closeOnClick: false,
    closeOnMove: true
  });

  const providerSource = [
    { name: 'Network Servicing Providers', type: ProviderNetworkType.Network },
  ];

  const serviceAreaRegionIdToRegionCodeMap = new Map();

  const serviceArea = projectContext.runConfiguration.serviceArea.subRegions.map((sr) => {
    return {
      canonicalRegionSetId: sr.canonicalRegionSetId,
      displayName: sr.displayName,
      regionCode: sr.regionCode,
      regionGranularity: sr.regionGranularity,
      regionSetId: sr.regionSetId,
      subRegions: sr.subRegions.map((sub) => {
        serviceAreaRegionIdToRegionCodeMap.set(sub.regionSetId.toString(), { countyCode: sub.regionCode, stateCode: sr.regionCode });

        return {
          canonicalRegionSetId: sub.canonicalRegionSetId,
          displayName: sub.displayName,
          regionCode: sub.regionCode,
          regionGranularity: sub.regionGranularity,
          regionSetId: sub.regionSetId,
          status: null as RegionSetStatusType
        }
      })
    };
  });

  const uniqueRegionSetIds = Array.from(new Set(serviceArea.flatMap(sa => sa.subRegions.map(sr => sr.regionSetId))));
  const keyFor = (status: { regionSetId: number }) => status.regionSetId;

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const getStatuses = async (): Promise<{ [key: string]: RegionSetStatusType }> => {
      return (await projectService.getStatuses(projectContext.project.projectId, uniqueRegionSetIds, projectContext.runConfiguration.projectRunConfigurationId))
        .reduce<{ [regionKey: number]: RegionSetStatusType }>((acc, status) => ({ ...acc, [keyFor(status)]: status.status }), {});
    }

    getStatuses().then((statuses) => {
      serviceArea.map((sa) => (
        sa.subRegions.map((sr) => (
          sr.status = statuses[keyFor(sr)] ?? null
        ))
      ))
    });
  }, [])

  useEffect(() => {
    setSelectedSpecialty(getFilterValue(projectContext.filters, FilterType.Specialty));
    setSelectedServiceArea(getCountyFilterValue(projectContext.filters, FilterType.County));
  }, [projectContext.filters]);

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      accessToken: mappingContext.mapboxApiKey,
      bearing: bearing,
      center: [longitude, latitude],
      container: mapContainer.current as HTMLElement,
      dragRotate: false,
      maxZoom: 16,
      minZoom: 3,
      pitch: pitch,
      preserveDrawingBuffer: true,
      projection: {
        name: 'mercator',
      },
      scrollZoom: {
        around: 'center'
      },
      style: selectedMapStyle,
      touchPitch: false,
      zoom: zoom,
    });

    map.current.setMaxBounds(mappingContext.defaultBoundaries);

    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');

    map.current.on('move', () => {
      setLongitude(map.current.getCenter().lng.toFixed(6));
      setLatitude(map.current.getCenter().lat.toFixed(6));
      setZoom(map.current.getZoom().toFixed(2));
      setPitch(map.current.getPitch().toFixed(2));
    });

    //Initialize Layers and Sources.  Map generation will populate source data and set layer properties.
    map.current.once('load', () => {
      InitializeMapContent();
    });

    setSelectedSpecialty(getFilterValue(projectContext.filters, FilterType.Specialty));
    setSelectedServiceArea(getCountyFilterValue(projectContext.filters, FilterType.County));

    return () => map.current.remove();
  }, [])

  const InitializeMapContent = () => {
    addImages();

    if (!map.current.getSource(countyBordersSource)) {
      map.current.addSource(countyBordersSource, {
        data: { features: [], type: nameof<FeatureCollection>() },
        type: MAPBOX_TYPE_GEOJSON
      });
    }

    if (!map.current.getLayer(countyBordersLayer)) {
      map.current.addLayer({
        id: countyBordersLayer,
        paint: {
          "line-color": '#402294',
          "line-width": 1.5,
        },
        source: countyBordersSource,
        type: MAPBOX_TYPE_LINE,
      });
    }

    if (!map.current.getSource(providersSource)) {
      map.current.addSource(providersSource, {
        cluster: true,
        clusterMaxZoom: providersClusterMaxZoom,
        clusterRadius: providersClusterRadius,
        data: { features: [], type: nameof<FeatureCollection<Point, NetworkProviderMapResults>>() },
        generateId: true,
        type: MAPBOX_TYPE_GEOJSON,
      });
    }

    if (!map.current.getLayer(multipleProvidersLayer)) {
      map.current.addLayer({
        filter: ['has', MAPBOX_CLUSTER_POINT_COUNT],
        id: multipleProvidersLayer,
        layout: {
          "icon-allow-overlap": true,
          "icon-image": MULTIPLE_PROVIDERS_IMAGE,
          "icon-size": .05,
          visibility: "none"
        },
        source: providersSource,
        type: MAPBOX_TYPE_SYMBOL,
      }, countyBordersLayer);
    }

    if (!map.current.getLayer(singleProvidersLayer)) {
      map.current.addLayer({
        filter: ['!', ['has', MAPBOX_CLUSTER_POINT_COUNT]],
        id: singleProvidersLayer,
        layout: {
          "icon-allow-overlap": true,
          "icon-image": PROVIDER_IMAGE,
          "icon-size": .05,
          visibility: "none"
        },
        source: providersSource,
        type: MAPBOX_TYPE_SYMBOL,
      }, multipleProvidersLayer);
    }

    if (!map.current.getSource(unclusteredMembersSource)) {
      map.current.addSource(unclusteredMembersSource, {
        data: { features: [], type: nameof<FeatureCollection<Point, MemberMapResults>>() },
        generateId: true,
        type: MAPBOX_TYPE_GEOJSON,
      });
    }

    if (!map.current.getSource(clusteredMembersWithAccessSource)) {
      map.current.addSource(clusteredMembersWithAccessSource, {
        cluster: true,
        clusterMaxZoom: membersClusterMaxZoom,
        clusterRadius: membersClusterRadius,
        data: { features: [], type: nameof<FeatureCollection<Point, MemberMapResults>>() },
        filter: ['==', ['get', nameof<MemberMapResults>(o => o.covered)], true],
        generateId: true,
        type: MAPBOX_TYPE_GEOJSON,
      });
    }

    if (!map.current.getSource(clusteredMembersWithoutAccessSource)) {
      map.current.addSource(clusteredMembersWithoutAccessSource, {
        cluster: true,
        clusterMaxZoom: membersClusterMaxZoom,
        clusterRadius: membersClusterRadius,
        data: { features: [], type: nameof<FeatureCollection<Point, MemberMapResults>>() },
        filter: ['==', ['get', nameof<MemberMapResults>(o => o.covered)], false],
        generateId: true,
        type: MAPBOX_TYPE_GEOJSON,
      });
    }

    if (!map.current.getLayer(clusteredMembersWithoutAccessClusterCountLayer)) {
      map.current.addLayer({
        filter: ['has', MAPBOX_CLUSTER_POINT_COUNT],
        id: clusteredMembersWithoutAccessClusterCountLayer,
        layout: {
          "icon-allow-overlap": true,
          "text-allow-overlap": true,
          'text-field': ['get', MAPBOX_CLUSTER_POINT_COUNT_ABBREVIATED],
          'text-size': 18,
          visibility: "none"
        },
        source: clusteredMembersWithoutAccessSource,
        type: MAPBOX_TYPE_SYMBOL,
      }, singleProvidersLayer);
    }

    if (!map.current.getLayer(clusteredMembersWithoutAccessClusteredLayer)) {
      map.current.addLayer({
        filter: ['has', MAPBOX_CLUSTER_POINT_COUNT],
        id: clusteredMembersWithoutAccessClusteredLayer,
        layout: {
          visibility: "none"
        },
        paint: {
          "circle-color": "#fff",
          "circle-radius": clusterStepFunction(2),
          'circle-stroke-color': '#CC3A52',
          'circle-stroke-width': clusterStepFunction(2)
        },
        source: clusteredMembersWithoutAccessSource,
        type: MAPBOX_TYPE_CIRCLE,
      }, clusteredMembersWithoutAccessClusterCountLayer);
    }

    if (!map.current.getLayer(clusteredMembersWithoutAccessPointLayer)) {
      map.current.addLayer({
        filter: ['!', ['has', MAPBOX_CLUSTER_POINT_COUNT]],
        id: clusteredMembersWithoutAccessPointLayer,
        layout: {
          visibility: "none"
        },
        paint: {
          "circle-color": "#fff",
          "circle-radius": 4,
          'circle-stroke-color': '#CC3A52',
          'circle-stroke-width': 5
        },
        source: clusteredMembersWithoutAccessSource,
        type: MAPBOX_TYPE_CIRCLE,
      }, clusteredMembersWithoutAccessClusteredLayer);
    }

    if (!map.current.getLayer(clusteredMembersWithAccessClusterCountLayer)) {
      map.current.addLayer({
        filter: ['has', MAPBOX_CLUSTER_POINT_COUNT],
        id: clusteredMembersWithAccessClusterCountLayer,
        layout: {
          "icon-allow-overlap": true,
          "text-allow-overlap": true,
          'text-field': ['get', MAPBOX_CLUSTER_POINT_COUNT_ABBREVIATED],
          'text-size': 18,
          visibility: "none"
        },
        paint: {
          "text-color": "#FFFFFF"
        },
        source: clusteredMembersWithAccessSource,
        type: MAPBOX_TYPE_SYMBOL,
      }, clusteredMembersWithoutAccessPointLayer);
    }

    if (!map.current.getLayer(clusteredMembersWithAccessClusteredLayer)) {
      map.current.addLayer({
        filter: ['has', MAPBOX_CLUSTER_POINT_COUNT],
        id: clusteredMembersWithAccessClusteredLayer,
        layout: {
          visibility: "none"
        },
        paint: {
          "circle-color": "#008559",
          "circle-radius": clusterStepFunction(),
          'circle-stroke-color': '#006a47',
          'circle-stroke-width': 1
        },
        source: clusteredMembersWithAccessSource,
        type: MAPBOX_TYPE_CIRCLE,
      }, clusteredMembersWithAccessClusterCountLayer);
    }

    if (!map.current.getLayer(clusteredMembersWithAccessPointLayer)) {
      map.current.addLayer({
        filter: ['!', ['has', MAPBOX_CLUSTER_POINT_COUNT]],
        id: clusteredMembersWithAccessPointLayer,
        layout: {
          visibility: "none"
        },
        paint: {
          "circle-color": "#008559",
          "circle-radius": 9,
          'circle-stroke-color': '#006a47',
          'circle-stroke-width': 1
        },
        source: clusteredMembersWithAccessSource,
        type: MAPBOX_TYPE_CIRCLE,
      }, clusteredMembersWithAccessClusteredLayer);
    }

    if (!map.current.getLayer(unclusteredMembersWithoutAccessLayer)) {
      map.current.addLayer({
        filter: ["==", ["get", nameof<MemberMapResults>(o => o.covered)], false],
        id: unclusteredMembersWithoutAccessLayer,
        layout: {
          visibility: "none"
        },
        paint: {
          "circle-color": "#fff",
          "circle-radius": 4,
          'circle-stroke-color': '#CC3A52',
          'circle-stroke-width': 5
        },
        source: unclusteredMembersSource,
        type: MAPBOX_TYPE_CIRCLE,
      }, clusteredMembersWithAccessPointLayer);
    }

    if (!map.current.getLayer(unclusteredMembersWithAccessLayer)) {
      map.current.addLayer({
        filter: ["==", ["get", nameof<MemberMapResults>(o => o.covered)], true],
        id: unclusteredMembersWithAccessLayer,
        layout: {
          visibility: "none"
        },
        paint: {
          "circle-color": "#008559",
          "circle-radius": 9,
          'circle-stroke-color': '#006a47',
          'circle-stroke-width': 1
        },
        source: unclusteredMembersSource,
        type: MAPBOX_TYPE_CIRCLE,
      }, unclusteredMembersWithoutAccessLayer);
    }
  }

  useEffect(() => {
    if (map.current) {
      map.current.setStyle(selectedMapStyle);
      map.current.once('style.load', () => {
        InitializeMapContent();

        if (membersLayerInitialized) {
          addMemberLayers();
        }
        if (providersLayerInitialized) {
          addProviderLayers();
        }

        if (countyBorderLayerInitialized) {
          addCountyBorderLayers();
        }

        map.current.on('mouseenter', singleProvidersLayer, showSingleProviderPopup);
        map.current.on('mouseleave', singleProvidersLayer, hideSingleProviderPopup);
        map.current.on('mouseenter', multipleProvidersLayer, showMultiProviderPopup);
        map.current.on('click', singleProvidersLayer, singleProviderClick);
        map.current.on('click', multipleProvidersLayer, multiProvidersClick);
      });
    }
  }, [selectedMapStyle]);

  useEffect(() => {
    if (!flyoutContext.isFlyoutVisible && flyoutHasOpened) {
      // Below is not a typo. We need to set the icon back to the default image for all single providers.
      map.current.setLayoutProperty(singleProvidersLayer, 'icon-image', PROVIDER_IMAGE);
      setFlyoutHasOpened(false);
    }
  }, [flyoutContext.isFlyoutVisible])

  useEffect(() => {
    setMapButtonsAreDisabled(!(!!selectedServiceArea && !!selectedSpecialty && !!selectedProviderSource));
  }, [selectedServiceArea, selectedSpecialty]);

  useEffect(() => {
    if (map.current && !!providersSourceBBox && !!countyBordersSourceBBox && !!memberSourceBBox)
      zoomToBoundingBox(providersSourceBBox, countyBordersSourceBBox, memberSourceBBox);
  }, [providersSourceBBox, countyBordersSourceBBox, memberSourceBBox]);

  const showSingleProviderPopup = (e: mapboxgl.MapMouseEvent) => {
    const feature = e.features[0].toJSON() as Feature<Point, NetworkProviderMapResults>;
    map.current.getCanvas().style.cursor = 'auto';

    if (multiProviderPopup.isOpen()) {
      hideMultiProviderPopup();
    }

    const coordinates = feature.geometry.coordinates.slice() as LngLatLike;
    const properties = feature.properties;
    const addressInfo = `${[properties.address, properties.address2, properties.city, properties.state].filter(x => !!x).join(", ")} ${properties.zip}`;
    const html = `
          <br />
          <div class="popup-title">${properties.displayName}</div>
          <div class="popup-info"><span class="popup-label">NPI:</span> <span class="popup-value">${properties.npi}</span></div>
          <div class="popup-info"><span class="popup-label">Address:</span> <span class="popup-value">${addressInfo}</span></div>
          <div class="popup-info"><span class="popup-label">Serves Percent:</span> <span class="popup-value">${properties.servesPercent}%</span></div>`;

    singleProviderPopup
      .setLngLat(coordinates)
      .setHTML(html)
      .addTo(map.current);
  };

  const hideSingleProviderPopup = () => {
    map.current.getCanvas().style.cursor = '';
    singleProviderPopup.remove();
  };

  const singleProviderClick = (e: mapboxgl.MapMouseEvent) => {
    const feature = e.features[0].toJSON() as Feature<Point, NetworkProviderMapResults>;

    // Be careful when changing the logic below. "feature.id" is the unique id that is automatically generated
    // for each feature in the GeoJSON source by mapbox via generateId: true. It is used to match the provider
    // icon to the provider in the "servicingProvider" object
    map.current.setLayoutProperty(singleProvidersLayer, 'icon-image',
      [
        'match',
        ['id'],
        feature.id,
        PROVIDER_HIGHLIGHT_IMAGE,
        PROVIDER_IMAGE
      ]
    );

    buildServicingProvider(feature);
    buildProjectOptions();
    flyoutContext.handleOpen();
    setFlyoutHasOpened(true);
  };

  const showMultiProviderPopup = (e: mapboxgl.MapMouseEvent, newCoords?: LngLatLike) => {
    const feature = e.features[0].toJSON();
    let html = '';
    map.current.getCanvas().style.cursor = 'auto';

    // If we're not at max zoom level, show a message to the user that they can click to
    // view more information which will set a bounding box around the cluster and zoom in
    if (map.current.getZoom() < providersClusterMaxZoom) {
      const point_count = feature.properties.point_count;
      html = `
        <div>
          <div class="popup-info"><span class="popup-label">${point_count} providers at this location</span><br /></div>
          <div><span tabIndex="0" class="popup-more-info">Click to view more information</span></div>
        </div>
      `;

      setTimeout(() => {
        document.querySelectorAll('.popup-more-info').forEach((element) => {
          element.addEventListener('click', () => {
            const clusterId = feature.properties.cluster_id;
            const source = map.current.getSource(providersSource) as mapboxgl.GeoJSONSource;

            source.getClusterLeaves(clusterId, point_count, 0, (error, features) => {
              map.current.fitBounds(bbox({
                features: features,
                type: 'FeatureCollection',
              }), { padding: 150 });
            });
          });
        });
      }, 10);

      multiProviderPopup
        .setLngLat(e.lngLat)
        .setHTML(html)
        .addTo(map.current);
    } else {
      // At max zoom level and hovering over a cluster
      if (feature.properties.cluster_id) {
        const clusterId = feature.properties.cluster_id;
        const source = map.current.getSource(providersSource) as mapboxgl.GeoJSONSource;
        const point_count = feature.properties.point_count;

        source.getClusterLeaves(clusterId, point_count, 0, (error, features) => {
          const coordinates = ((feature.geometry as Point).coordinates.slice() as LngLatLike);
          const html = buildMultiProviderPopup(features as Feature<Point, NetworkProviderMapResults>[]);

          multiProviderPopup
            .setLngLat(newCoords ? newCoords : coordinates)
            .setHTML(html)
            .addTo(map.current);
        });
      }
    }
  };

  const buildMultiProviderPopup = (features: Feature<Point, NetworkProviderMapResults>[]): string => {
    const provider = features[0] as Feature<Point, NetworkProviderMapResults>;
    const properties = provider.properties;

    let addressInfo = `${[properties.address, properties.address2].filter(x => !!x).join("<br />")}`;
    addressInfo += `<br />${[properties.city, properties.state].filter(x => !!x).join(", ")} ${properties.zip}`;


    const html = `
      <div class="popup-title">${addressInfo}</div>
      <div class="popup-info"><span class="popup-label">Number of Providers:</span> <span>${features.length}</span></div>
      <div class="popup-table">
        <div>
          <span class="popup-name-header">Name</span>
          <span class="popup-npi-header">NPI</span>
        </div>
        ${features.map((feature: Feature<Point, NetworkProviderMapResults>) => {
      const { displayName, npi } = feature.properties;
      return `
            <div class="popup-row">
              <span tabIndex="0" class="popup-value" data-properties='${JSON.stringify(feature)}'>
                ${displayName}
              </span>
              <span>${npi}</span>
            </div>
          `;
    }).join('')}
      </div>
    `;

    setTimeout(() => {
      document.querySelectorAll('.popup-value[data-properties]').forEach((element) => {
        element.addEventListener('click', (event) => {
          const feature = JSON.parse((event.target as HTMLElement).dataset.properties) as Feature<Point, NetworkProviderMapResults>;
          handleMultiNameClick(feature);
        });
      });
    }, 10);

    return html;
  };

  const handleMultiNameClick = (feature: Feature<Point, NetworkProviderMapResults>) => {
    buildServicingProvider(feature);
    buildProjectOptions();
    flyoutContext.handleOpen();
    setFlyoutHasOpened(true);
  }

  const hideMultiProviderPopup = () => {
    map.current.getCanvas().style.cursor = '';
    multiProviderPopup.remove();
  };

  const multiProvidersClick = (e: mapboxgl.MapMouseEvent) => {
    const zoomLevel = map.current.getZoom();

    if (zoomLevel < providersClusterMaxZoom) {
      map.current.easeTo({
        center: e.lngLat,
        zoom: zoomLevel + 1
      });

      setZoom(zoomLevel + 1);
      return;
    }
  };

  useEffect(() => {
    if (map.current && !!providersSourceBBox && !!countyBordersSourceBBox && !!memberSourceBBox)
      zoomToBoundingBox(providersSourceBBox, countyBordersSourceBBox, memberSourceBBox);
  }, [providersSourceBBox, countyBordersSourceBBox, memberSourceBBox]);

  const generateMap = () => {
    setCountyBordersSourceBBox(null);
    setProvidersSourceBBox(null);
    setMemberSourceBBox(null);
    memberData.current = null;
    providerData.current = null;
    borderData.current = null;
    addLayersControl();
    addLegendControl();
    addMemberLayers();
    addProviderLayers();
    addCountyBorderLayers();
    getRegionDetails();
  }

  const removeControls = (controlIds: string[]) => {
    controlIds.forEach((controlId) => {
      map.current.removeControl(controlId);
    });

    map.current._layersControl = null;
    map.current._legendControl = null;
  }

  const resetLayers = (layerIds: string[]) => {
    layerIds.forEach((layerId) => {
      if (!map.current.getLayer(layerId)) return;
      map.current.setLayoutProperty(layerId, 'visibility', 'none');
    });
  }

  const resetSources = (sourceIds: string[]) => {
    sourceIds.forEach((sourceId) => {
      if (!map.current.getSource(sourceId)) return;
      map.current.getSource(sourceId).setData({ features: [], type: nameof<FeatureCollection>() });
    });
  }

  const removeImages = (imageIds: string[]) => {
    imageIds.forEach((imageId) => {
      if (!map.current.hasImage(imageId)) return;
      map.current.removeImage(imageId);
    });
  }

  const addLayersControl = () => {
    if (map.current._layersControl) return;

    const layersConfig: ProviderMemberLayerControlConfig = {
      members: [
        {
          checked: true,
          clusteredLayerIds: [clusteredMembersWithAccessPointLayer, clusteredMembersWithAccessClusteredLayer, clusteredMembersWithAccessClusterCountLayer],
          label: 'with Access',
          unclusteredLayerIds: [unclusteredMembersWithAccessLayer]
        },
        {
          checked: true,
          clusteredLayerIds: [clusteredMembersWithoutAccessPointLayer, clusteredMembersWithoutAccessClusteredLayer, clusteredMembersWithoutAccessClusterCountLayer],
          label: 'without Access',
          unclusteredLayerIds: [unclusteredMembersWithoutAccessLayer]
        },
      ],
      onClusteringCheckboxChange: (checked: boolean) => setClusterMembers(checked),
      onStyleChange: (style: string) => setSelectedMapStyle(style),
      providers: [
        { checked: true, label: 'Single Providers', layerId: singleProvidersLayer },
        { checked: true, label: 'Multiple Providers', layerId: multipleProvidersLayer }
      ]
    };

    const layersControl = new ProviderMemberLayerControl(layersConfig);
    map.current.addControl(layersControl, 'top-right');
    map.current._layersControl = layersControl;
  }

  useEffect(() => {
    updateMembersLayerVisibility();
  }, [clusterMembers])

  const addLegendControl = () => {
    if (map.current._legendControl) return;

    const legendConfig: ProviderMemberLegendControlConfig = {
      members: [
        { image: `${imagesDir}${MEMBERS_WITH_PNG}`, label: 'with Access' },
        { image: `${imagesDir}${MEMBERS_WITHOUT_PNG}`, label: 'without Access' },
      ],
      providers: [
        { image: `${imagesDir}${PROVIDER_PNG}`, label: 'Single Providers' },
        { image: `${imagesDir}${MULTIPLE_PROVIDERS_PNG}`, label: 'Multiple Providers' }
      ],
    };

    const legendControl = new ProviderMemberLegendControl(legendConfig);
    map.current.addControl(legendControl, 'bottom-right');
    map.current._legendControl = legendControl;
  }

  const addImages = () => {
    removeImages([MEMBERS_WITH_IMAGE, MEMBERS_WITHOUT_IMAGE, PROVIDER_IMAGE, PROVIDER_HIGHLIGHT_IMAGE, MULTIPLE_PROVIDERS_IMAGE]);

    map.current.loadImage(`${imagesDir}${MEMBERS_WITH_PNG}`, (error: Error, image: HTMLImageElement) => {
      if (error) throw error;

      map.current.addImage(MEMBERS_WITH_IMAGE, image);
    });

    map.current.loadImage(`${imagesDir}${MEMBERS_WITHOUT_PNG}`, (error: Error, image: HTMLImageElement) => {
      if (error) throw error;

      map.current.addImage(MEMBERS_WITHOUT_IMAGE, image);
    });

    map.current.loadImage(`${imagesDir}${PROVIDER_PNG}`, (error: Error, image: HTMLImageElement) => {
      if (error) throw error;

      map.current.addImage(PROVIDER_IMAGE, image);
    });

    map.current.loadImage(`${imagesDir}${PROVIDER_HIGHLIGHT_PNG}`, (error: Error, image: HTMLImageElement) => {
      if (error) throw error;

      map.current.addImage(PROVIDER_HIGHLIGHT_IMAGE, image);
    });

    map.current.loadImage(`${imagesDir}${MULTIPLE_PROVIDERS_PNG}`, (error: Error, image: HTMLImageElement) => {
      if (error) throw error;

      map.current.addImage(MULTIPLE_PROVIDERS_IMAGE, image);
    });
  };

  const addMemberLayers = async () => {
    try {
      setMemberIsLoading(true);

      if (memberData.current === null) {
        memberData.current = await memberSetService.GetMemberResultsAsync(
          projectContext.project.projectId,
          projectContext.runConfiguration.projectRunConfigurationId,
          selectedServiceArea,
          selectedSpecialty
        );
      }

      setMembersLayerInitialized(true);

      map.current.getSource(clusteredMembersWithAccessSource).setData(memberData.current);
      map.current.getSource(clusteredMembersWithoutAccessSource).setData(memberData.current);
      map.current.getSource(unclusteredMembersSource).setData(memberData.current);
      setMemberSourceBBox(bbox(memberData.current));

      updateMembersLayerVisibility();
    } catch (error) {
      console.error('Error fetching member results:', error);
    } finally {
      setMemberIsLoading(false);
    }
  };

  const updateMembersLayerVisibility = () => {
    if (!map.current._layersControl ||
      !map.current.getSource(clusteredMembersWithAccessSource) ||
      !map.current.getSource(clusteredMembersWithoutAccessSource) ||
      !map.current.getSource(unclusteredMembersSource) ||
      !map.current.getLayer(clusteredMembersWithAccessPointLayer) ||
      !map.current.getLayer(clusteredMembersWithAccessClusteredLayer) ||
      !map.current.getLayer(clusteredMembersWithAccessClusterCountLayer) ||
      !map.current.getLayer(clusteredMembersWithoutAccessPointLayer) ||
      !map.current.getLayer(clusteredMembersWithoutAccessClusteredLayer) ||
      !map.current.getLayer(clusteredMembersWithoutAccessClusterCountLayer) ||
      !map.current.getLayer(unclusteredMembersWithAccessLayer) ||
      !map.current.getLayer(unclusteredMembersWithoutAccessLayer))
      return;


    const clusteredMembersWithVisibility = map.current._layersControl && map.current._layersControl.config.members[0].checked && clusterMembers ? "visible" : "none";
    const clusteredMembersWithoutVisibility = map.current._layersControl && map.current._layersControl.config.members[1].checked && clusterMembers ? "visible" : "none";
    const unclusteredMembersWithVisibility = map.current._layersControl && map.current._layersControl.config.members[0].checked && !clusterMembers ? "visible" : "none";
    const unclusteredMembersWithoutVisibility = map.current._layersControl && map.current._layersControl.config.members[1].checked && !clusterMembers ? "visible" : "none";

    map.current.setLayoutProperty(clusteredMembersWithAccessPointLayer, 'visibility', clusteredMembersWithVisibility);
    map.current.setLayoutProperty(clusteredMembersWithAccessClusteredLayer, 'visibility', clusteredMembersWithVisibility);
    map.current.setLayoutProperty(clusteredMembersWithAccessClusterCountLayer, 'visibility', clusteredMembersWithVisibility);
    map.current.setLayoutProperty(clusteredMembersWithoutAccessPointLayer, 'visibility', clusteredMembersWithoutVisibility);
    map.current.setLayoutProperty(clusteredMembersWithoutAccessClusteredLayer, 'visibility', clusteredMembersWithoutVisibility);
    map.current.setLayoutProperty(clusteredMembersWithoutAccessClusterCountLayer, 'visibility', clusteredMembersWithoutVisibility);

    map.current.setLayoutProperty(unclusteredMembersWithAccessLayer, 'visibility', unclusteredMembersWithVisibility);
    map.current.setLayoutProperty(unclusteredMembersWithoutAccessLayer, 'visibility', unclusteredMembersWithoutVisibility);
  };

  const addProviderLayers = async () => {
    try {
      setProviderIsLoading(true);

      if (providerData.current === null) {
        providerData.current = await projectService.servicingAccessPoints(
          projectContext.project.projectId,
          selectedServiceArea,
          projectContext.runConfiguration.projectRunConfigurationId,
          selectedSpecialty,
          ProviderNetworkType.Network,
          NetworkAssessmentAnalysisType.ServiceArea,
          GainsPercentEnum.All
        );
      }

      setProvidersLayerInitialized(true);

      map.current.getSource(providersSource).setData(providerData.current);
      setProvidersSourceBBox(bbox(providerData.current));

      const providerVisibility = map.current._layersControl && map.current._layersControl.config.providers[0].checked ? "visible" : "none";
      const multipleProvidersVisibility = map.current._layersControl && map.current._layersControl.config.providers[1].checked ? "visible" : "none";

      map.current.setLayoutProperty(singleProvidersLayer, 'visibility', providerVisibility);
      map.current.setLayoutProperty(multipleProvidersLayer, 'visibility', multipleProvidersVisibility);
    } catch (error) {
      console.error('Error fetching provider results:', error);
    } finally {
      setProviderIsLoading(false);
    }
  }

  const addCountyBorderLayers = async () => {
    try {
      setBordersAreLoading(true);

      if (borderData.current === null) {
        borderData.current = await geographyService.getCountyBordersByRegionSetId(selectedServiceArea) ??
          { features: [], type: 'FeatureCollection' };
      }

      setCountyBorderLayerInitialized(true);

      map.current.getSource(countyBordersSource).setData(borderData.current);

      map.current.setLayoutProperty(countyBordersLayer, 'visibility', "visible");

      setCountyBordersSourceBBox(bbox(borderData.current));
    } catch (error) {
      console.error('Error fetching county borders:', error);
    } finally {
      setBordersAreLoading(false);
    }
  }

  const buildServicingProvider = (feature: Feature<Point, NetworkProviderMapResults>): void => {
    const servicingProvider: ServicingProvider = {
      address: feature.properties?.address,
      address2: feature.properties?.address2,
      city: feature.properties?.city,
      contractType: null,
      countyFIPS: null,
      countySSA: null,
      credentials: feature.properties?.credentials,
      deactivatedNPI: false,
      displayName: feature.properties?.displayName,
      employmentStatus: null,
      entityType: feature.properties?.entityType,
      externLocationId: null,
      externProviderId: null,
      externRowId: null,
      externSpecialtyCode: null,
      externZip: null,
      firstName: feature.properties?.firstName,
      gender: null,
      geoInfoDisplay: null,
      invalidNPI: false,
      lastName: feature.properties?.lastName,
      latitude: feature.properties.latitude,
      longitude: feature.properties.longitude,
      medGroupAffiliation: null,
      medicareCCN: null,
      medicareCertifiedBeds: null,
      medicareOptOut: false,
      middleName: feature.properties?.middleName,
      missingNPI: false,
      name: feature.properties?.displayName,
      npi: feature.properties?.npi,
      oigExcluded: false,
      oigWaiver: false,
      organizationNPI: feature.properties?.organizationNPI,
      organizationName: feature.properties?.organizationName,
      planningRegion: null,
      precluded: false,
      prefix: feature.properties?.prefix,
      processed: false,
      providerId: feature.properties.providerId,
      servesPercent: feature.properties?.servesPercent,
      specialty: null,
      specialtyCode: feature.properties?.specialtyCode,
      state: feature.properties?.state,
      suffix: feature.properties?.suffix,
      taxonomyCode: null,
      tin: null,
      underContract: null,
      zip: feature.properties?.zip,
    };

    setProvider(servicingProvider);
  }

  const buildProjectOptions = (): void => {
    const projectOptions: ProjectOptions = {
      canPublish: projectContext.project.canPublish,
      displayOnNetworkAccordion: projectContext.project.displayOnNetworkAccordion,
      enableRegulatoryExceptionReports: projectContext.project.enableRegulatoryExceptionReports,
      excludeAccuracyPractitionerAtLocationInspector: projectContext.project.excludeAccuracyPractitionerAtLocationInspector,
      excludeInVolumeActions: projectContext.project.excludeInVolumeActions,
      excludeProviderClaimsActivityInspector: projectContext.project.excludeProviderClaimsActivityInspector,
      hasUnprocessedProjectionProviders: projectContext.project.hasUnprocessedProjectionProviders,
      hasUnprocessedProviderTerminations: projectContext.project.hasUnprocessedProviderTerminations,
      hasUnprocessedSpecialtyGroupExceptions: projectContext.project.hasUnprocessedSpecialtyGroupExceptions,
      hasUnpublishedResults: projectContext.project.hasUnpublishedResults,
      hasZipSummaries: projectContext.project.hasZipSummaries,
      includeGapAging: projectContext.project.includeGapAging,
      includeInEnterpriseComplianceDashboard: projectContext.project.includeInEnterpriseComplianceDashboard,
      isCompanionRunning: projectContext.project.isCompanionRunning,
      isEditRestricted: projectContext.project.isEditRestricted,
      isReadOnly: projectContext.project.isReadOnly,
      isRunning: projectContext.project.isRunning,
      projectChangeReasons: projectContext.project.projectChangeReasons,
      projectId: projectContext.project.projectId,
      projectNeedsFullRun: projectContext.project.projectNeedsFullRun,
      projectRunConfigurations: projectContext.project.projectRunConfigurations,
      resource: projectContext.project.resource,
      showAccuracyGapAnalysis: projectContext.project.showAccuracyGapAnalysis,
      showAdvancedComparison: projectContext.project.showAdvancedComparison,
      showAffiliationsInsights: projectContext.project.showAffiliationsInsights,
      showAttestation: projectContext.project.showAttestation,
      showAverageDistance: projectContext.project.showAverageDistance,
      showAverageTime: projectContext.project.showAverageTime,
      showClosestProviderReport: projectContext.project.showClosestProviderReport,
      showLocationConfidenceScores: projectContext.project.showLocationConfidenceScores,
      showMaximumDistance: projectContext.project.showMaximumDistance,
      showMaximumTime: projectContext.project.showMaximumTime,
      showMipsScore: projectContext.project.showMipsScore,
      showMotiveAppropriatenessScore: projectContext.project.showMotiveAppropriatenessScore,
      showNextClosestNetworkProviders: projectContext.project.showNextClosestNetworkProviders,
      showNppesDates: projectContext.project.showNppesDates,
      showPatternOfCareProviders: projectContext.project.showPatternOfCareProviders,
      showPciDashboard: projectContext.project.showPciDashboard,
      showPciData: projectContext.project.showPciData,
      showTricareClaimsVolumeColumn: projectContext.project.showTricareClaimsVolumeColumn,
      showVaClaimsVolumeColumn: projectContext.project.showVaClaimsVolumeColumn,
      strictTimeAndDistance: projectContext.project.strictTimeAndDistance,
      templateId: projectContext.project.templateId,
    };

    setProject(projectOptions);
  }

  const clearMapAndFilters = () => {
    removeControls([map.current._layersControl, map.current._legendControl]);
    resetLayers([
      clusteredMembersWithAccessPointLayer,
      clusteredMembersWithoutAccessPointLayer,
      singleProvidersLayer,
      multipleProvidersLayer,
      countyBordersLayer,
      clusteredMembersWithAccessClusteredLayer,
      clusteredMembersWithoutAccessClusteredLayer,
      clusteredMembersWithAccessClusterCountLayer,
      clusteredMembersWithoutAccessClusterCountLayer,
      unclusteredMembersWithAccessLayer,
      unclusteredMembersWithoutAccessLayer
    ]);
    //Reset Flyout-related icon-image changes.
    map.current.setLayoutProperty(singleProvidersLayer, 'icon-image', PROVIDER_IMAGE);

    // Create new history location by removing all filters from the url
    projectContext.filters.forEach((filter) => {
      if (filter.urlPart) {
        history.location.pathname = history.location.pathname.replace(filter.urlPart, '');
      }
    });

    history.push(history.location.pathname);
    projectContext.filters = [];
    resetSources([clusteredMembersWithAccessSource, providersSource, countyBordersSource, clusteredMembersWithoutAccessSource, unclusteredMembersSource]);

    setCountyBordersSourceBBox(null);
    setProvidersSourceBBox(null);
    setMemberSourceBBox(null);

    setLatitude(mappingContext.defaultLatitude);
    setLongitude(mappingContext.defaultLongitude);
    setPitch(mappingContext.defaultPitch);
    setBearing(mappingContext.defaultBearing);
    setSelectedServiceArea('');
    setSelectedSpecialty('');
    setSelectedProviderSource(ProviderNetworkType.Network);
    setMembersLayerInitialized(false);
    setProvidersLayerInitialized(false);
    setCurrentAdequacySummarySpecialtyGroup(null);
    setCountyBorderLayerInitialized(false);
    setClusterMembers(false);

    memberData.current = null;
    providerData.current = null;
    borderData.current = null;

    map.current.setZoom(mappingContext.defaultZoom);
    map.current.panTo([mappingContext.defaultLongitude, mappingContext.defaultLatitude], { duration: 1000 });
    setSelectedMapStyle(defaultMapStyle);
  }

  const getProviderProfile = (): React.ReactNode => {
    return (
      <ServicingProviderProfile
        isProjected={false}
        isTerminated={false}
        nextClosestProvider={false}
        project={project}
        provider={provider}
        regionSetId={parseInt(selectedServiceArea)}
        runConfiguration={projectContext.runConfiguration}
        providerNetworkType={ProviderNetworkType.Network}
        specialtyGroupCode={selectedSpecialty}
        isSSAToFIPSFeatureEnabled={featureFlagContext.enabledFeatureFlags.has(Features.SSA_TO_FIPS)}
      />
    );
  }

  const getRegionDetails = async () => {
    const regionDetails: AdequacySummaryByRegionSet[] =
      await projectService.regionDetailsByRegionSet(
        projectContext.project.projectId,
        Number(selectedServiceArea),
        projectContext.runConfiguration.projectRunConfigurationId,
        selectedSpecialty,
        NetworkAssessmentAnalysisType.ServiceArea);

    const selectedSpecialtyGroupSummary = regionDetails[0].specialtyGroupSummaries[0];
    setCurrentAdequacySummarySpecialtyGroup(selectedSpecialtyGroupSummary);
  }

  const mergeBBoxes = (bboxOne: number[], bboxTwo: number[], bboxThree: number[]) => {
    return [
      Math.min(bboxOne[0], bboxTwo[0], bboxThree[0]), //Farthest x to the left
      Math.min(bboxOne[1], bboxTwo[1], bboxThree[1]), //Farthest y to the bottom
      Math.max(bboxOne[2], bboxTwo[2], bboxThree[2]), //Farthest x to the right
      Math.max(bboxOne[3], bboxTwo[3], bboxThree[3]) //Farthest y to the top
    ];
  }

  const zoomToBoundingBox = (providerBoundingBox: number[], countyBordersBoundingBox: number[], memberSourceBBox: number[]) => {
    const combinedBBox = mergeBBoxes(providerBoundingBox, countyBordersBoundingBox, memberSourceBBox);
    map.current.fitBounds(combinedBBox, { padding: 150 });
  }

  const getPathParameter = (path: string, param: string): string => {
    const pathSplit = path.split('/');
    const paramIndex = pathSplit.indexOf(param);
    if (paramIndex > -1 && paramIndex < pathSplit.length - 1)
      return pathSplit[paramIndex + 1];
  }

  const getFilterValue = (filters: RegionFilterItem[], filterTypeValue: FilterType): string => {
    const filterItem = filters.find(filter => filter.filterType === filterTypeValue);

    if (!!filterItem)
      return filterItem.filterValue;

    // If no county filter is found, it might still be in the url.  Attempt to parse it from the url.
    const filterPathValue = getPathParameter(location?.pathname, filterTypeValue.replace(/\//g, ""));
    if (!filterPathValue) return '';

    return filterPathValue;
  };

  const getCountyFilterValue = (filters: RegionFilterItem[], filterTypeValue: FilterType): string => {
    const countyFilterValue = getFilterValue(filters, filterTypeValue);

    if (!countyFilterValue) return '';

    //Since the value is a county code, we have to dig for the region set id with our code
    const countyMapEntry = [...serviceAreaRegionIdToRegionCodeMap].find(([, value]) => value.countyCode === countyFilterValue);
    return !!countyMapEntry ? countyMapEntry[0] : '';
  };

  const replaceFilterUrl = (filterType: FilterType, currentValue: string, newValue: string) => {
    if (!newValue) {
      if (!currentValue) return;

      if (history.location.pathname.includes(filterType)) {
        history.push(history.location.pathname
          .replaceAll(filterType + currentValue, "")
          .replaceAll(filterType + getPathParameter(history.location.pathname, filterType.replace(/\//g, "")), ""));
      }
    } else if (history.location.pathname.includes(filterType)) {
      history.push(history.location.pathname
        .replaceAll(filterType + currentValue, "")
        .replaceAll(filterType + getPathParameter(history.location.pathname, filterType.replace(/\//g, "")), "")
        .concat(filterType + newValue));
    } else {
      history.push(`${history.location.pathname}${filterType + newValue}`);
    }

    projectContext.loadedFilters = false;
  }

  const clusterStepFunction = (divisor: number = 1) => {
    return [
      'step',
      ['get', MAPBOX_CLUSTER_POINT_COUNT],
      clusterRadiusSmall / divisor,
      clusterRadiusLowerStep,
      clusterRadiusMedium / divisor,
      clusterRadiusUpperStep,
      clusterRadiusLarge / divisor
    ];
  }

  return (
    <>
      <div className="d-flex mb-3 row justify-content-start">
        <div className="col-12 col-md-auto">
          <QESSelect
            allowEmpty={true}
            testId="provider-member-mapping__filter--service-area-dropdown"
            label="Service Area"
            isValid={selectedServiceArea !== ''}
            onChange={(e) => {
              const current = serviceAreaRegionIdToRegionCodeMap.get(selectedServiceArea);
              const selected = serviceAreaRegionIdToRegionCodeMap.get(e.target.value);
              replaceFilterUrl(
                FilterType.State, current?.stateCode, selected?.stateCode);
              replaceFilterUrl(
                FilterType.County, current?.countyCode, selected?.countyCode);

              setSelectedServiceArea(e.target.value);
            }}
            value={selectedServiceArea}
          >
            {serviceArea.map((sa) => (
              <optgroup key={sa.regionCode} label={sa.displayName}>
                {sa.subRegions.map((sub) => (
                  <option
                    key={sub.regionSetId}
                    value={sub.regionSetId}
                  >
                    {sub.displayName}
                  </option>
                ))}
              </optgroup>
            ))}
          </QESSelect>
        </div>
        <div className="col-12 col-md-auto">
          <QESSelect
            allowEmpty={true}
            testId="provider-member-mapping__filter--specialty-dropdown"
            label="Specialty"
            isValid={selectedSpecialty !== ''}
            onChange={(e) => {
              replaceFilterUrl(FilterType.Specialty, selectedSpecialty, e.target.value);
              setSelectedSpecialty(e.target.value);
            }}
            value={selectedSpecialty}
          >
            {sortedSpecialtyGroups.map((sp) => (
              <option
                key={sp.code}
                value={sp.code}
              >
                {sp.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col-12 col-md-auto">
          <QESSelect
            allowEmpty={false}
            testId="provider-member-mapping__filter--provider-source-dropdown"
            label="Provider Source"
            onChange={(e) => setSelectedProviderSource(providerSource.find((ps) => ps.type === e.target.value)?.type ?? ProviderNetworkType.Network)}
            value={selectedProviderSource}
          >
            {providerSource.map((ps) => (
              <option
                key={ps.type}
                value={ps.type}
              >
                {ps.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <span className="d-flex justify-content-end col-12 col-xl provider-member-mapping__filter-buttons">
          <QESButton
            qesType="secondary"
            disabled={mapButtonsAreDisabled}
            onClick={generateMap}
            qesTestId="provider-member-mapping__generate"
          >
            Generate
          </QESButton>
          <QESButton
            qesType="secondary"
            qesTestId="provider-member-mapping__clear-all"
            disabled={mapButtonsAreDisabled}
            onClick={clearMapAndFilters}
          >
            Clear All
          </QESButton>
        </span>
      </div>
      <div>
        {currentAdequacySummarySpecialtyGroup
          && (<MapExport
            isLoading={isLoading}
            specialty={currentAdequacySummarySpecialtyGroup.footnote.specialtyGroupName}
            serviceArea={currentAdequacySummarySpecialtyGroup.footnote.geographyName}
            runConfigurationDateCreated={projectContext.runConfiguration.dateCreated}
            projectName={projectContext.project.resource.name}
          />)
        }
        <div className="provider-member-mapping__map-container">
          {currentAdequacySummarySpecialtyGroup
            && (<MapDetailsBox
              adequacySummarySpecialtyGroup={currentAdequacySummarySpecialtyGroup}
              cmsCreditIsApplying={projectContext.cmsCreditIsApplying}
              project={projectContext.project}
              runConfiguration={projectContext.runConfiguration} />)
          }
          <div ref={mapContainer} className="provider-member-mapping__map-display">
            {isLoading && (
              <div className="loading">
                <LoadingSpinner />
              </div>
            )}
          </div>
          
        </div>
        <Flyout
          content={{
            body: getProviderProfile() as JSX.Element,
            title: '',
          }}
          size="40"
          triggerElement={mapContainer}
        />
      </div>
    </>
  )
}

export const ProviderMemberMapping: FC<unknown> = () => (
  <FlyoutContextProvider>
    <WrappedProviderMemberMapping />
  </FlyoutContextProvider>
);